/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React from 'react';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { formatDateString } from 'utilities/date-utils';
import ReportLink from 'components/ReportsModule/components/common/ReportLink';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { KeyClassroomEngagementStudentResult } from 'types/schema-generated-types';
import { ReportEntityType } from 'components/ReportsModule/types';

const getSpotCheckColumns: (t: TFunction) => ColumnProps<any>[] = t => [
  {
    id: 'lastName',
    label: t('Student'),
    sortKey: 'student.lastName',
    getValue: item => (
      <ReportLink
        key={item.student.id}
        entityType={ReportEntityType.STUDENT}
        entityId={item.student.id}
      >
        {`${item.student.firstName} ${item.student.lastName}`}
      </ReportLink>
    ),
    sortable: true
  },
  {
    id: 'gradeLevel',
    label: t('Grade'),
    helpText: t('gradeLevel', { ns: 'ReportsHelpCommon' }),
    sortKey: 'student.gradeLevel',
    getValue: item => <Typography>{t(item.student.gradeLevel, { ns: 'GradeLevel' })}</Typography>,
    sortable: true
  },
  {
    id: 'curriculumWeek',
    label: t('Current Curriculum Week'),
    helpText: t('curriculumWeek', { ns: 'ReportsHelpCommon' }),
    sortKey: 'curriculumWeek',
    getValue: item => (
      <Typography>
        {item.curriculumWeek ? t('Week {{weekNumber}}', { weekNumber: item.curriculumWeek }) : '-'}
      </Typography>
    ),
    sortable: true
  },
  {
    id: 'totalCompletedCount',
    label: t('Spot Checks Completed'),
    helpText: t('spotChecksCompleted', { ns: 'ReportsHelpTableColumns' }),
    sortKey: 'spotChecks.totalCompletedCount',
    getValue: item => <Typography>{item.spotChecks?.totalCompletedCount ?? 0}</Typography>,
    sortable: true
  },
  {
    id: 'dateLastCompleted',
    label: t('Last Spot Check Completed'),
    helpText: t('spotCheckLastCompleted', { ns: 'ReportsHelpTableColumns' }),
    sortKey: 'spotChecks.dateLastCompleted',
    getValue: item => (
      <Typography>
        {item.spotChecks?.dateLastCompleted
          ? formatDateString(new Date(item.spotChecks.dateLastCompleted))
          : '-'}
      </Typography>
    ),
    sortable: true
  },
  {
    id: 'averageWpm',
    label: t('Average Speed (WPM)'),
    helpText: t('wpmSpotCheck', { ns: 'ReportsHelpCommon' }),
    sortKey: 'spotChecks.averageWpm',
    getValue: item => <Typography>{item.spotChecks?.averageWpm ?? '-'}</Typography>,
    sortable: true
  },
  {
    id: 'averageAccuracy',
    label: t('Average Accuracy (%)'),
    helpText: t('accuracySpotCheck', { ns: 'ReportsHelpCommon' }),
    sortKey: 'spotChecks.averageAccuracy',
    getValue: item => (
      <Typography>
        {item.spotChecks?.averageAccuracy ? `${item.spotChecks.averageAccuracy}%` : '-'}
      </Typography>
    ),
    sortable: true
  }
];

const SpotCheckTableCard: React.FC<{
  reportData: KeyClassroomEngagementStudentResult[];
  loading: boolean;
}> = ({ reportData = [], loading }) => {
  const { t } = useTranslation([
    'Reports',
    'ReportsHelpCommon',
    'ReportsHelpTableColumns',
    'GradeLevel'
  ]);
  const columns = getSpotCheckColumns(t);

  return (
    <ReportCard
      title={t('Spot Checks')}
      loading={loading}
      reportData={reportData}
      reportDataProp="spotChecks"
    >
      <LwtTable columns={columns} data={reportData} />
    </ReportCard>
  );
};

export default SpotCheckTableCard;
